/**
 * AFS Legal OS — Contract Engine — Knowledge-Tier Resolution
 *
 * Roadmap ref: 2d (Contract Engine — Draft Mode)
 *
 * Wires each clause generated by 2c through a per-clause library lookup and
 * tags it with a KnowledgeTier:
 *   STATUTORY_LIBRARY  — the library returned a match above threshold for
 *                        this clause's subject in its jurisdiction.
 *   GENERAL_KNOWLEDGE  — library was silent, but the clause is standard
 *                        drafting practice that doesn't turn on
 *                        jurisdiction-specific authority (e.g. counterparts,
 *                        notices boilerplate).
 *   RESEARCH_NEEDED    — library was silent AND the clause touches an area
 *                        where jurisdiction-specific authority matters.
 *
 * Deliberately out of scope here, per the roadmap:
 *   - the [RESEARCH NEEDED] per-authority-type checklist itself → 2e
 *     (this module tags a clause RESEARCH_NEEDED but never writes to
 *     `research_needed` — that field stays untouched until 2e runs)
 *   - flag_tier (STATUTORY / GENERAL_PRUDENCE / CLIENT_INSTRUCTION) → 2f
 *
 * Two-step flow per clause, same separation-of-concerns pattern as 2b:
 *   Step 1 — queryLibrary() directly against the clause's own text, filtered
 *            by the clause's denormalized `jurisdiction` (per 1a/1d). A hit
 *            is a hit — no AI judgment call needed, so clauses with a
 *            library match are tagged STATUTORY_LIBRARY immediately and
 *            never enter the classification step below.
 *   Step 2 — for the remaining clauses (library silent), ONE batched
 *            classification call decides GENERAL_KNOWLEDGE vs
 *            RESEARCH_NEEDED for all of them together, rather than one AI
 *            call per silent clause. This keeps a contract with (say) 40
 *            clauses and 12 library misses to 1 classification call
 *            instead of 12.
 *
 * Exported as a plain async function — no React/hook dependency — same
 * convention as pass0.ts / clauseRegister.ts.
 */

import { callClaude, withRetry, classifyError } from '@/services/api';
import { queryLibrary } from '@/services/library';
import { loadContractClauses, saveContractClause } from '@/storage/helpers';
import type { ContractClause, KnowledgeTier } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface KnowledgeTierInput {
  contract_id: string;

  /**
   * Optional — improves the library query and the classification prompt's
   * context, but not required. Not stored on ContractClause (unlike
   * jurisdiction, contract_type is deliberately not denormalized per 1c),
   * so callers that have a Pass0Result handy should pass its
   * `contract_type` through here.
   */
  contract_type?: string;
}

export interface KnowledgeTierResult {
  contract_id: string;
  clauses:     ContractClause[];   // full updated register, all clauses, in clause_number order
  tagged:      number;             // count of clauses newly tagged this run
  ok:          boolean;
  /** Set only when ok is false. */
  error?:      string;
}

interface ClassificationVerdict {
  clause_number: string;
  tier:          'GENERAL_KNOWLEDGE' | 'RESEARCH_NEEDED';
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 — PER-CLAUSE LIBRARY LOOKUP
// ─────────────────────────────────────────────────────────────────────────────

function buildClauseQuery(clause: ContractClause, contractType?: string): string {
  const prefix = contractType ? `${contractType} — ` : '';
  return `${prefix}${clause.clause_text}`.slice(0, 300);
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — BATCH CLASSIFICATION FOR LIBRARY-SILENT CLAUSES
// ─────────────────────────────────────────────────────────────────────────────

const CLASSIFY_SYSTEM = `You are Contract Engine Draft Mode, Knowledge-Tier Resolution — Step 2.
You receive a list of contract clauses for which the library was searched
and returned no authoritative match. For each clause, decide whether it is:

  "GENERAL_KNOWLEDGE" — standard drafting practice. A competent drafter can
    write this correctly from general contract-drafting convention; it
    does not turn on jurisdiction-specific statute, regulation, or case
    law. Boilerplate (counterparts, notices, entire agreement, severability,
    assignment, headings) is almost always this tier, as is any clause
    whose content follows from the deal facts rather than from law.

  "RESEARCH_NEEDED" — the library was silent, but this clause touches an
    area where jurisdiction-specific legal authority matters (statutory
    minimums/maximums, mandatory terms, regulator-imposed requirements,
    a doctrine that varies meaningfully by jurisdiction, or anything with
    legal consequences if drafted against the wrong rule). Flag it here
    rather than guessing at the underlying law.

Do not draft or rewrite any clause text. Do not explain your reasoning
outside the JSON. Do not wrap the JSON in markdown code fences — return raw
JSON only.

Return EXACTLY this shape, nothing else, with one entry per input clause,
same clause_number values, any order:
{
  "verdicts": [
    { "clause_number": string, "tier": "GENERAL_KNOWLEDGE" | "RESEARCH_NEEDED" },
    ...
  ]
}`;

function buildClassifyUserMsg(
  clauses: ContractClause[],
  contractType?: string,
): string {
  const lines: string[] = [];
  if (contractType) lines.push(`Contract type: ${contractType}`);
  lines.push(`Jurisdiction: ${clauses[0]?.jurisdiction ?? '(unknown)'}`);
  lines.push('');
  lines.push('CLAUSES (library returned no match for any of these):');
  for (const c of clauses) {
    lines.push(`[${c.clause_number}] ${c.clause_text}`);
  }
  return lines.join('\n');
}

/** Strips optional markdown code fences and validates the required shape. Never throws. */
function parseVerdicts(raw: string): ClassificationVerdict[] | null {
  try {
    const stripped = raw.trim()
      .replace(/^```(?:json)?/i, '')
      .replace(/```$/, '')
      .trim();
    const parsed = JSON.parse(stripped) as Record<string, unknown>;
    const verdicts = parsed.verdicts;
    if (!Array.isArray(verdicts) || verdicts.length === 0) return null;

    const out: ClassificationVerdict[] = [];
    for (const v of verdicts) {
      const clauseNumber = (v as Record<string, unknown>)?.clause_number;
      const tier         = (v as Record<string, unknown>)?.tier;
      if (typeof clauseNumber !== 'string' || !clauseNumber.trim()) return null;
      if (tier !== 'GENERAL_KNOWLEDGE' && tier !== 'RESEARCH_NEEDED') return null;
      out.push({ clause_number: clauseNumber.trim(), tier });
    }
    return out;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────

function blankResult(contractId: string, error: string): KnowledgeTierResult {
  return {
    contract_id: contractId,
    clauses:     [],
    tagged:      0,
    ok:          false,
    error,
  };
}

/**
 * Runs Knowledge-Tier Resolution over every clause currently on file for
 * `contract_id`. Never throws — a failed library call, AI call, malformed
 * response, or storage failure degrades to `ok: false` with a user-facing
 * `error` string, same convention as runPass0() / runClauseRegisterGeneration().
 *
 * Idempotent-ish: clauses that already carry a non-null knowledge_tier are
 * left untouched and re-returned as-is (not re-queried, not re-tagged) —
 * safe to call again after 2c regenerates only some clauses, or after a
 * partial prior run failed partway through.
 */
export async function runKnowledgeTierResolution(
  input: KnowledgeTierInput,
): Promise<KnowledgeTierResult> {
  const { contract_id, contract_type } = input;

  let clauses: ContractClause[];
  try {
    clauses = await loadContractClauses(contract_id);
  } catch (e) {
    return blankResult(contract_id, classifyError(e));
  }

  if (clauses.length === 0) {
    return blankResult(contract_id, 'No clauses on file for this contract — run 2c first');
  }

  const pending = clauses.filter(c => c.knowledge_tier == null);
  if (pending.length === 0) {
    return { contract_id, clauses, tagged: 0, ok: true };
  }

  // ── Step 1 — per-clause library lookup ─────────────────────────────────────
  const libLookups = await Promise.all(pending.map(async (clause) => {
    const result = await queryLibrary(
      buildClauseQuery(clause, contract_type),
      {
        filter:    { jurisdiction: clause.jurisdiction },
        topK:      3,
        threshold: 0.68,
      },
    );
    const hit = result.ok && result.matches.length > 0;
    return { clause, hit };
  }));

  const resolved: ContractClause[] = [];
  const silent: ContractClause[] = [];

  for (const { clause, hit } of libLookups) {
    if (hit) {
      resolved.push({ ...clause, knowledge_tier: 'STATUTORY_LIBRARY' as KnowledgeTier });
    } else {
      silent.push(clause);
    }
  }

  // ── Step 2 — batch classification for library-silent clauses ───────────────
  if (silent.length > 0) {
    let verdicts: ClassificationVerdict[] | null;
    try {
      const { text } = await withRetry(() => callClaude({
        system:      CLASSIFY_SYSTEM,
        userMsg:     buildClassifyUserMsg(silent, contract_type),
        maxTokens:   2000,
        skipLibrary: true,   // reasoning about clause content, not a library query — see file header
      }));
      verdicts = parseVerdicts(text);
    } catch (e) {
      return blankResult(contract_id, classifyError(e));
    }

    if (!verdicts) {
      return blankResult(contract_id, 'Could not parse knowledge-tier verdicts from AI response');
    }

    const verdictByNumber = new Map(verdicts.map(v => [v.clause_number, v.tier]));
    for (const clause of silent) {
      const tier = verdictByNumber.get(clause.clause_number);
      if (!tier) {
        return blankResult(
          contract_id,
          `AI response was missing a verdict for clause ${clause.clause_number}`,
        );
      }
      resolved.push({ ...clause, knowledge_tier: tier as KnowledgeTier });
    }
  }

  // ── Persist ──────────────────────────────────────────────────────────────
  const now = new Date().toISOString();
  const toSave = resolved.map(c => ({ ...c, updated_at: now }));
  const saveResults = await Promise.all(toSave.map(saveContractClause));
  const failedCount = saveResults.filter(ok => !ok).length;
  if (failedCount === toSave.length) {
    return blankResult(contract_id, 'Resolved knowledge tiers but failed to save them — check storage');
  }

  const savedById = new Map(toSave.map(c => [c.id, c]));
  const finalClauses = clauses
    .map(c => savedById.get(c.id) ?? c)
    .sort((a, b) => a.clause_number.localeCompare(b.clause_number, undefined, { numeric: true }));

  return {
    contract_id,
    clauses: finalClauses,
    tagged:  toSave.length,
    ok:      true,
    ...(failedCount > 0
      ? { error: `${failedCount} of ${toSave.length} newly-tagged clauses failed to save locally` }
      : {}),
  };
}
